import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import './AdvancedSearch.css';
import { getWriters, getAdvancedSearch } from "../../api";

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const autoSearchStarted = useRef(false);
  const [writers, setWriters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    author: '',
    selectedAuthor: '',
    title: '',
    content: '',
    keywords: [],
    timeRange: '',
    school: '',
    type: '',
    topic: '',
    selectedTopic: ''
  });

  // Anahtar kelime input'u için ayrı state
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    const fetchWriters = async () => {
      try {
        const response = await getWriters();
        if (response && response.data) {
          setWriters(response.data);
        }
      } catch (error) {
        console.error('Yazarlar yüklenirken hata oluştu:', error);
      }
    };

    fetchWriters();
  }, []);

  // Örnek veriler (gerçek verilerle değiştirilecek)
  const topics = [
    'Eğitim',
    'Tarih',
    'Kültür',
    'Sanat',
    'Edebiyat',
    'Bilim',
    'Teknoloji'
  ];

  const timeRanges = [
    '1920-1930',
    '1931-1940',
    '1941-1950',
    '1951-1960',
    '1961-1970',
    '1971-1980'
  ];

  const schools = [
    'İstanbul Üniversitesi',
    'Ankara Üniversitesi',
    'Ege Üniversitesi',
    'Marmara Üniversitesi',
    'Hacettepe Üniversitesi',
    'Boğaziçi Üniversitesi'
  ];

  const types = [
    'Makale',
    'Tez',
    'Bildiri',
    'Kitap',
    'Dergi',
    'Rapor'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Anahtar kelime input'u için özel handler
  const handleKeywordInputChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Enter veya virgül ile anahtar kelime ekleme
  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  // Anahtar kelime ekleme
  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !searchParams.keywords.includes(keyword)) {
      setSearchParams(prev => ({
        ...prev,
        keywords: [...prev.keywords, keyword]
      }));
      setKeywordInput('');
    }
  };

  // Anahtar kelime silme
  const removeKeyword = (keywordToRemove) => {
    setSearchParams(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('🚀 Form submit edildi');
    console.log('📝 Ham searchParams:', searchParams);
    
    // Boş olmayan parametreleri filtrele
    const validParams = Object.entries(searchParams)
      .filter(([key, value]) => {
        if (key === 'keywords') {
          return value.length > 0; // keywords array'i boş değilse
        }
        return value.trim() !== ''; // diğer alanlar için
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    console.log('✅ Filtrelenmiş validParams:', validParams);
    
    if (Object.keys(validParams).length === 0) {
      console.warn('⚠️ Hiç arama parametresi girilmedi!');
      alert('Lütfen en az bir arama kriteri giriniz.');
      return;
    }

    // API çağrısı yap
    performAdvancedSearch(validParams);
  };

  const performAdvancedSearch = async (validParams) => {
    try {
      setIsLoading(true);
      
      console.log('🔍 Gelişmiş Arama Parametreleri:', validParams);
      console.log('📡 API çağrısı yapılıyor...');
      
      // Backend'in beklediği formata uygun parametre mapping
      const apiParams = { ...validParams };
      
      // keywords -> keyword (backend'de keyword bekleniyor)
      if (apiParams.keywords) {
        apiParams.keyword = apiParams.keywords.join(',');
        delete apiParams.keywords;
      }
      
      console.log('🔄 API için dönüştürülmüş parametreler:', apiParams);
      
      const response = await getAdvancedSearch(apiParams);
      
      console.log('✅ API Response alındı:', response);
      console.log('📊 Response data:', response.data || response);
      console.log('📈 Sonuç sayısı:', (response.data || response)?.length || 'Bilinmiyor');
      
      // Sonuçları URL parametreleri olarak geçir
      const searchQuery = new URLSearchParams(validParams).toString();
      
      // SearchResults sayfasına yönlendir ve sonuçları state olarak geçir
      navigate(`/aramaSonuclari?${searchQuery}&advanced=true`, {
        state: { searchResults: response.data || response, searchParams: validParams }
      });
    } catch (error) {
      console.error('❌ Gelişmiş arama sırasında hata oluştu:', error);
      console.error('🔍 Hata detayları:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Hata durumunda da yönlendir (backend hazır olmadığında)
      const searchQuery = new URLSearchParams(validParams).toString();
      navigate(`/aramaSonuclari?${searchQuery}&advanced=true`);
    } finally {
      setIsLoading(false);
      console.log('🏁 Arama işlemi tamamlandı');
    }
  };

  useEffect(() => {
    const raw = (urlSearchParams.get('keyword') || urlSearchParams.get('keywords') || '').trim();
    if (!raw || autoSearchStarted.current) return;
    const keywords = raw.split(',').map((k) => k.trim()).filter(Boolean);
    if (!keywords.length) return;
    autoSearchStarted.current = true;
    setSearchParams((prev) => ({ ...prev, keywords }));
    performAdvancedSearch({ keywords });
  }, [urlSearchParams]);

  return (
    <div className="gelismisArama-container">
      <h1 className="search-title">Gelişmiş Arama</h1>
      <p className="search-description">
        Arama özelliğini kullanarak ilgili maddeleri bulabilirsiniz. Arama, bir veya birkaç alanı doldurarak veya açılır listelerden bir öğe seçerek yapılabilir.
      </p>

      <form onSubmit={handleSubmit} className="search-form">
        <div className="form-row">
          {/* <div className="form-group">
            <label>Yazar</label>
            <input
              type="text"
              name="author"
              value={searchParams.author}
              onChange={handleInputChange}
              placeholder="Yazar adı giriniz"
              className="form-input"
            />
          </div> */}
          <div className="form-group">
            <label>Yazar İsimleri</label>
            <select
              name="selectedAuthor"
              value={searchParams.selectedAuthor}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Seçiniz</option>
              {writers.map((writer) => (
                <option key={writer.id} value={writer.id}>{writer.name} {writer.surname}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Başlık</label>
          <input
            type="text"
            name="title"
            value={searchParams.title}
            onChange={handleInputChange}
            placeholder="Başlık giriniz"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Metin</label>
          <input
            type="text"
            name="content"
            value={searchParams.content}
            onChange={handleInputChange}
            placeholder="Metin içinde aranacak kelime"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>Anahtar Kelimeler</label>
          <input
            type="text"
            name="keywordInput"
            value={keywordInput}
            onChange={handleKeywordInputChange}
            onKeyPress={handleKeywordKeyPress}
            onBlur={addKeyword}
            placeholder="Anahtar kelimeleri giriniz (Enter veya virgül ile ayırın)"
            className="form-input"
          />
          
          {/* Anahtar kelime tag'ları */}
          {searchParams.keywords.length > 0 && (
            <div className="keywords-container" style={{ marginTop: '10px' }}>
              {searchParams.keywords.map((keyword, index) => (
                <span 
                  key={index} 
                  className="keyword-tag"
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '4px 8px',
                    margin: '2px 4px 2px 0',
                    borderRadius: '16px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb'
                  }}
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    style={{
                      background: 'none',
                      border: 'none',
                      marginLeft: '6px',
                      cursor: 'pointer',
                      color: '#1976d2',
                      fontWeight: 'bold'
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {/* <div className="form-row">
          <div className="form-group">
            <label>Zaman Çizelgesi</label>
            <select
              name="timeRange"
              value={searchParams.timeRange}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Zaman</option>
              {timeRanges.map((range, index) => (
                <option key={index} value={range}>{range}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Okul</label>
            <select
              name="school"
              value={searchParams.school}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Okul</option>
              {schools.map((school, index) => (
                <option key={index} value={school}>{school}</option>
              ))}
            </select>
          </div>
        </div> */}

        {/* <div className="form-row">
          <div className="form-group">
            <label>Biçim</label>
            <select
              name="type"
              value={searchParams.type}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Tip</option>
              {types.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tip</label>
            <select
              name="selectedTopic"
              value={searchParams.selectedTopic}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Seçiniz</option>
              {topics.map((topic, index) => (
                <option key={index} value={topic}>{topic}</option>
              ))}
            </select>
          </div>
        </div> */}

        {/* <div className="form-group">
          <label>Konum</label>
          <input
            type="text"
            name="topic"
            value={searchParams.topic}
            onChange={handleInputChange}
            placeholder="Konum giriniz"
            className="form-input"
          />
        </div> */}

        <div className="search-info">
          
            <button type="submit" className="advanced-search-button" disabled={isLoading}>
            {isLoading ? 'Aranıyor...' : 'Ara'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdvancedSearch; 